import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  mergeMap,
  withLatestFrom,
  tap,
  map,
} from 'rxjs/operators';
import { ApkService } from '@core/services';
import { Apk, ApkForm } from '@core/models';
import { selectApkForm, selectApkId } from '@store/selectors';
import {
  APK_CREATE,
  APK_CREATE_COMPLETED,
  APK_CREATE_VIEW,
  APK_EDIT_MODE,
  APK_FIND_ALL,
  APK_FIND_ALL_SUCCESS,
  APK_UPDATE_LOADING,
  APK_FAILURE,
  APK_UPDATE_VIEW,
  APK_UPDATE_ID,
  APK_UPDATE,
  APK_UPDATE_COMPLETED,
  APK_REMOVE,
  APK_REMOVE_COMPLETED,
  APK_FIND,
  APK_FIND_SUCCESS,
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
} from '@store/actions';

@Injectable()
export class ApkEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private apkService: ApkService
  ) {}

  createView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_CREATE_VIEW),
      mergeMap(() => [
        APK_EDIT_MODE({ editMode: true, isEditing: false }),
      ])
    )
  );

  updateView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_UPDATE_VIEW),
      mergeMap(({ apkId }) => [
        APK_UPDATE_ID({ apkId }),
        APK_EDIT_MODE({ editMode: true, isEditing: true }),
      ])
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_FIND),
      mergeMap(({ id }) => {
        this.store.dispatch(APK_UPDATE_LOADING({ loading: true }));
        return this.apkService.find(id).pipe(
          mergeMap(({ data }) => {
            const [apk] = data as Apk[];
            return [
              APK_FIND_SUCCESS({ apk }),
              APK_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              APK_FAILURE({ error }),
              APK_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_FIND_ALL),
      mergeMap(({ moduleType }) => {
        this.store.dispatch(APK_UPDATE_LOADING({ loading: true }));
        return this.apkService.findAll(moduleType).pipe(
          mergeMap(({ data }) => {
            const apkList = data as Apk[];
            return [
              APK_FIND_ALL_SUCCESS({ apkList }),
              APK_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              APK_FAILURE({ error }),
              APK_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_CREATE),
      withLatestFrom(this.store.select(selectApkForm)),
      filter(([_, apkForm]) => !!apkForm),
      map(([_, apkForm]) => ({ apkForm: apkForm as ApkForm })),
      mergeMap(({ apkForm }) => {
        this.store.dispatch(APK_UPDATE_LOADING({ loading: true }));
        return this.apkService.create(apkForm).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Apk created successfully',
              },
            }),
            APK_CREATE_COMPLETED(),
            APK_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              APK_FAILURE({ error }),
              APK_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  createComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(APK_CREATE_COMPLETED),
        tap(() => {
          this.apkService.createCompleted();
        })
      ),
    { dispatch: false }
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_UPDATE),
      withLatestFrom(
        this.store.select(selectApkForm),
        this.store.select(selectApkId)
      ),
      filter(([_, apkForm, apkId]) => !!apkForm && !!apkId),
      map(([_, apkForm, apkId]) => ({ apkForm: apkForm as ApkForm, apkId })),
      mergeMap(({ apkForm, apkId }) => {
        this.store.dispatch(APK_UPDATE_LOADING({ loading: true }));
        return this.apkService.update(apkId!, apkForm).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Apk updated successfully',
              },
            }),
            APK_UPDATE_COMPLETED(),
            APK_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              APK_FAILURE({ error }),
              APK_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  updateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(APK_UPDATE_COMPLETED),
        tap(() => {
          this.apkService.updateCompleted();
        })
      ),
    { dispatch: false }
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APK_REMOVE),
      mergeMap(({ id }) => {
        this.store.dispatch(APK_UPDATE_LOADING({ loading: true }));
        return this.apkService.remove(id).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Apk removed successfully',
              },
            }),
            APK_REMOVE_COMPLETED(),
            APK_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              APK_FAILURE({ error }),
              APK_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  removeComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(APK_REMOVE_COMPLETED),
        tap(() => {})
      ),
    { dispatch: false }
  );
}
